import { Component, ElementRef, EventEmitter, OnInit, Output, ViewChild, inject, input } from '@angular/core';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { RadarChart } from 'echarts/charts';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent,
  ToolboxComponent,
  LegendComponent
} from 'echarts/components';
import { ItemService } from 'src/app/services/item.service';
import { Item } from 'src/app/models/item.model';
import { ItemCategory } from 'src/app/models/item-category';
import { CURRENT_PREFIX, FUTURE_PREFIX, itemCategoryToHexColor } from 'src/app/utils/item.util';
import { getCustomIcon } from 'src/app/utils/custom-icon.util';
import { CustomIcon } from 'src/app/models/custom-icon.model';
import { translateItemCategory } from 'src/app/utils/item.util';
import { ContentCardComponent } from '../../shared-modules/content-card/content-card.component';
import { NgClass } from '@angular/common';
import { MatFormField, MatInput } from '@angular/material/input';
import { CustomIconComponent } from '../../shared-modules/custom-icon/custom-icon.component';
import { BreadcrumbComponent, BreadcrumbItem } from '../../shared-modules/breadcrumb/breadcrumb.component';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent,
  LabelLayout,
  UniversalTransition,
  CanvasRenderer,
  ToolboxComponent,
  LegendComponent,
  RadarChart
]);

@Component({
    selector: 'app-spiderweb',
    templateUrl: './spiderweb.component.html',
    styleUrls: ['./spiderweb.component.scss'],
    imports: [ContentCardComponent, NgClass, MatFormField, MatInput, CustomIconComponent, BreadcrumbComponent]
})
export class SpiderwebComponent implements OnInit {
  private itemService = inject(ItemService);


  public items!: Item[];
  public currentSituationItems!: Item[];
  public selectedItem?: { currentItem: Item, futureItem: Item };
  public translatedCategory?: string;
  public numberOfItemsClass = 'normal-items';

  readonly isPdfExport = input<boolean>(false);

  get breadcrumbs(): BreadcrumbItem[] {
    return [
      { label: 'Home', link: '/landing-page' },
      { label: 'Resultaten - Spiderweb' }
    ];
  }
  private indicators!: { name: string; max: number; }[];
  private seriesData!: { current: object[], future: object[] };
  
  private chart?: echarts.EChartsType;

  @ViewChild("chart") set chartElement(chartRef: ElementRef) {
    if(this.chart) return;
    
    this.chart = echarts.init(chartRef.nativeElement);
    this.chart.resize();
  }

  public chartWidth?: number;
  public chartHeight?: number;

  @Output() public chartReady = new EventEmitter<boolean>();


  public ngOnInit() {
    window.addEventListener('resize', () => {
      console.log("Resizing chart...");
      this.chart?.resize();
    });

    this.itemService.getItemsWithContentAsString().subscribe(items => {
      this.items = this.sortItems(items.filter(item => item.enabled));

      const currentSituationItems = this.items.filter(item => item.isCurrentSituation);
      this.numberOfItemsClass = `items-${currentSituationItems.length}`;
      this.setSelectedItem(currentSituationItems[currentSituationItems.length - 1]);
      this.indicators = this.getIndicators(currentSituationItems);
      this.currentSituationItems = structuredClone(currentSituationItems).reverse();

      this.seriesData = this.getSeriesData(this.items);
      this.initChart();
    });
  }

  public getTheme(item: Item): string {
    return item.type.category + '-theme';
    }

  public iconClicked(itemId: number) {
    this.itemService.getItemWithContentAsString(itemId).subscribe(item => {
      this.setSelectedItem(item);
    });
  }

  get customIcon(): CustomIcon {
    return getCustomIcon(this.selectedItem!.currentItem.type.icon);
  }

  private sortItems(items: Item[]) {
    const adjustedOrder = [ItemCategory.HUMAN, ItemCategory.PUBLIC_TRANSPORT_HUB, ItemCategory.MOBILITY, ItemCategory.PLACE];
    return items
      .sort((a, b) => {
        return adjustedOrder.indexOf(a.type.category) - adjustedOrder.indexOf(b.type.category);
      });
  }

  private setSelectedItem(item: Item) {
    const futureItem = this.items.find(futureItem => futureItem.type.name === item.type.name)!;
    const selectedItem = { currentItem: item, futureItem };

    this.translatedCategory = translateItemCategory(item.type.category);
    this.selectedItem = selectedItem;
  }

  private getIndicators(items: Item[]): { name: string; max: number; }[] {
    return items.map(item => {
      return {
        name: item.type.name,
        max: 5,
        color: itemCategoryToHexColor(item.type.category)
      }
    });
  }

  private getSeriesData(items: Item[]): { current: object[], future: object[] } {
    const currentData: object[] = [];
    const futureData: object[] = [];
    Object.values(ItemCategory).forEach(category => {
      const currentCategoryScores = this.getCategoryScores(items, category, true);
      const futureCategoryScores = this.getCategoryScores(items, category, false);

      currentData.push(
        this.getDataEntry(category, currentCategoryScores, CURRENT_PREFIX)
      );
      futureData.push(
        this.getDataEntry(category, futureCategoryScores, FUTURE_PREFIX)
      );

    });
    return { current: currentData, future: futureData };
  }

  private getCategoryScores(items: Item[], category: ItemCategory, isCurrentSituation: boolean): (number | null)[] {
    const situationItems = items.filter(item => item.isCurrentSituation === isCurrentSituation);
    return situationItems.map(item => {
      const itemConsensusScore = item.consensusScore === undefined ? null : item.consensusScore;
      return item.type.category === category ? itemConsensusScore : null;
    });
  }

  private getDataEntry(category: ItemCategory, scores: (number | null)[], prefix: string) {
    return {
      name: `${prefix} - ${category}`,
      value: scores,
      itemStyle: {
        color: itemCategoryToHexColor(category)
      }
    }
  }

  private initChart() {
    const chartOptions = {
      tooltip: {
        trigger: 'axis'
      },
      legend: {
        show: false,
      },
      radar: [
        {
          startAngle: 105.5,
          indicator: this.indicators,
          axisName: {
            padding: 10,
            formatter: (value: string) => {
              return value.includes('Knooppuntdomeinen') ? value.replace(' ', '\n') : value;
            }
          },
        },
      ],
      series: [
        {
          type: 'radar',
          areaStyle: { opacity: 0.7 },
          data: this.seriesData.current
        },
        {
          type: 'radar',
          areaStyle: { opacity: 0.3 },
          data: this.seriesData.future
        }
      ]
    };
    this.chart?.setOption(chartOptions);
    const handler = () => {
      this.chart?.off('finished', handler); 
      setTimeout(() => {
        this.chartReady.emit(true);
      });
    };
    this.chart?.on('finished', handler);
  }
}
